import cn from 'classnames';
import React, { ChangeEvent, FC, ReactNode, memo } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';

import useStyles from './styles.module';

type Props = TextFieldProps & {
  label?: string | ReactNode;
  inputTitle?: string | ReactNode;
  value?: string | number | null;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  formClassName?: string;
  inputTitleClassName?: string;
  placeholder?: string;
  inputError?: string | null;
  unitEnd?: string | ReactNode;
  unitStart?: string | ReactNode;
  endAdornmentClass?: string;
  onMax?: () => void;
  disabled?: boolean;
  isSearch?: boolean;
  rows?: number;
  buttonMaxClassName?: string;
};

const Input: FC<Props> = ({
  label = '',
  inputTitle = '',
  value,
  onChange,
  formClassName = '',
  inputTitleClassName = '',
  buttonMaxClassName = '',
  disabled = false,
  placeholder = '',
  inputError = null,
  onMax = undefined,
  unitEnd = undefined,
  endAdornmentClass = '',
  unitStart = undefined,
  isSearch = false,
  rows = undefined,
  ...props
}) => {
  const { classes } = useStyles({
    isError: !!inputError,
  });

  return (
    <div className={classes.root}>
      {inputTitle && (
        <Typography
          variant="subtitle2"
          marginBottom="8px"
          className={inputTitleClassName}
        >
          {inputTitle}
        </Typography>
      )}
      <FormControl
        className={cn(
          classes.formControl,
          {
            [classes.disabledBox]: disabled,
            [classes.errorBox]: inputError,
          },
          formClassName,
        )}
      >
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          size="small"
          variant="outlined"
          className={classes.input}
          autoComplete="off"
          multiline={!!rows}
          rows={rows}
          {...props}
          InputProps={{
            endAdornment: unitEnd && (
              <InputAdornment position="end" className={endAdornmentClass}>
                <Typography variant="subtitle2">{unitEnd}</Typography>
              </InputAdornment>
            ),
            startAdornment: (unitStart || isSearch) && (
              <InputAdornment position="start" className={endAdornmentClass}>
                <Typography variant="subtitle2">
                  {isSearch ? (
                    <SearchIcon className={classes.searchIcon} />
                  ) : (
                    unitStart
                  )}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.actionsBox}>
          {onMax && (
            <button
              className={cn(classes.buttonMax, buttonMaxClassName)}
              onClick={onMax}
              disabled={disabled}
              type="button"
            >
              <Typography variant="caption2">MAX</Typography>
            </button>
          )}
        </div>
        {inputError && (
          <div className={cn(classes.supportText, classes.error)}>
            <Typography
              variant="caption1"
              color={(theme) => theme.palette.custom.accent.error}
            >
              {inputError}
            </Typography>
          </div>
        )}
      </FormControl>
    </div>
  );
};

export default memo(Input);
